import { useEffect, useState } from "react";
import { Tag, Plus, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, adminAuthHeaders } from "@/lib/api";
import { inputCls, labelCls } from "./constants";

const EMPTY = {
  code: "",
  label: "",
  discount_type: "percent",
  discount_value: 10,
  active: true,
  valid_until: "",
  usage_limit: null,
  applies_to: [],
};

export default function DiscountsSection({ brand, setBrand, phone_plans = [], mobile_internet_plans = [] }) {
  const [codes, setCodes] = useState([]);
  const [editing, setEditing] = useState(null); // null = form ukryty, {} = nowy, {id: ...} = edycja
  const [loading, setLoading] = useState(false);
  const all_plans = [...phone_plans, ...mobile_internet_plans];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/discount-codes", { headers: adminAuthHeaders() });
      setCodes(Array.isArray(data.codes) ? data.codes : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się wczytać kodów");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleFeature = (v) =>
    setBrand({ ...brand, discount_codes_enabled: v });

  const save = async () => {
    if (!editing) return;
    const payload = { ...editing };
    if (!payload.code) return toast.error("Podaj kod");
    if (!payload.discount_value || payload.discount_value <= 0) return toast.error("Podaj wartość rabatu > 0");
    // Konwersja pustych stringów na null
    payload.valid_until = payload.valid_until || null;
    payload.usage_limit = payload.usage_limit === "" || payload.usage_limit === undefined ? null : Number(payload.usage_limit);
    payload.discount_value = Number(payload.discount_value);
    payload.applies_to = Array.isArray(payload.applies_to) ? payload.applies_to : [];
    try {
      if (editing.id) {
        await api.put(`/admin/discount-codes/${editing.id}`, payload, { headers: adminAuthHeaders() });
        toast.success("Kod zaktualizowany");
      } else {
        await api.post("/admin/discount-codes", payload, { headers: adminAuthHeaders() });
        toast.success("Kod utworzony");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się zapisać");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć ten kod na stałe?")) return;
    try {
      await api.delete(`/admin/discount-codes/${id}`, { headers: adminAuthHeaders() });
      toast.success("Kod usunięty");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nie udało się usunąć");
    }
  };

  const togglePlan = (planId) => {
    const cur = editing.applies_to || [];
    const next = cur.includes(planId) ? cur.filter((x) => x !== planId) : [...cur, planId];
    setEditing({ ...editing, applies_to: next });
  };

  return (
    <section>
      <h3 className="flex items-center gap-2 font-bold text-slate-800 text-lg mb-4">
        <Tag size={18} className="text-[#dc2626]" /> Kody rabatowe
      </h3>
      <div className="rounded-xl border border-slate-200 p-5 bg-slate-50/50 space-y-5">
        {/* Główny switch */}
        <label className="flex items-center gap-3 p-3 bg-white rounded-lg border-2 border-[#dc2626]/30">
          <input
            data-testid="discounts-enabled"
            type="checkbox"
            checked={!!brand.discount_codes_enabled}
            onChange={(e) => toggleFeature(e.target.checked)}
            className="accent-[#dc2626] w-5 h-5"
          />
          <div>
            <div className="font-bold text-slate-800">Włącz pole „Kod rabatowy” w formularzu zamówienia</div>
            <div className="text-xs text-slate-500">
              Gdy włączone, na ekranie podsumowania (przed „Potwierdź”) pojawia się pole na kod rabatowy.
              Poprawny kod od razu przelicza cenę i pokazuje kwotę oszczędności.
            </div>
          </div>
        </label>

        {/* Lista kodów */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-bold text-slate-600 uppercase tracking-wider">Zdefiniowane kody</div>
            {editing === null && (
              <button
                type="button"
                data-testid="discount-add"
                onClick={() => setEditing({ ...EMPTY })}
                className="inline-flex items-center gap-1.5 bg-[#1f3a8a] hover:bg-[#15296b] text-white font-semibold text-sm px-3 py-2 rounded-full transition"
              >
                <Plus size={14} /> Dodaj kod
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-sm text-slate-400 italic">Wczytywanie…</div>
          ) : codes.length === 0 && editing === null ? (
            <div className="text-sm text-slate-400 italic p-3">Brak zdefiniowanych kodów. Kliknij „Dodaj kod”.</div>
          ) : (
            <div className="space-y-2">
              {codes.map((c) => (
                <div key={c.id} data-testid={`discount-row-${c.id}`} className="grid sm:grid-cols-[130px,1fr,120px,120px,80px,auto,auto] gap-2 items-center p-3 bg-white rounded-lg border border-slate-200 text-sm">
                  <span className="font-mono font-bold text-[#dc2626]">{c.code}</span>
                  <span className="text-slate-700 truncate">{c.label || <em className="text-slate-400">bez opisu</em>}</span>
                  <span className="text-slate-800 font-semibold">
                    {c.discount_type === "percent" ? `-${c.discount_value}%` : `-${c.discount_value} zł`}
                  </span>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full text-center ${c.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>
                    {c.active ? "Aktywny" : "Wyłączony"}
                  </span>
                  <span className="text-xs text-slate-500 text-center">{c.usage_count || 0}{c.usage_limit ? `/${c.usage_limit}` : ""}</span>
                  <button
                    type="button"
                    data-testid={`discount-edit-${c.id}`}
                    onClick={() => setEditing({ ...c, applies_to: c.applies_to || [] })}
                    className="text-[#1f3a8a] hover:text-[#15296b] font-semibold text-xs px-2"
                  >
                    Edytuj
                  </button>
                  <button
                    type="button"
                    data-testid={`discount-remove-${c.id}`}
                    onClick={() => remove(c.id)}
                    className="text-red-500 hover:text-red-700"
                    title="Usuń"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Formularz edycji / dodawania */}
        {editing !== null && (
          <div data-testid="discount-form" className="p-4 bg-white rounded-xl border-2 border-[#1f3a8a]/30 space-y-4">
            <div className="flex items-center justify-between">
              <div className="font-bold text-slate-800">{editing.id ? "Edytuj kod" : "Nowy kod rabatowy"}</div>
              <button type="button" onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700" title="Anuluj">
                <X size={18} />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Kod (będzie zapisany UPPERCASE)</label>
                <input
                  data-testid="discount-form-code"
                  className={inputCls + " uppercase font-mono"}
                  value={editing.code || ""}
                  onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                  placeholder="WIOSNA2026"
                />
              </div>
              <div>
                <label className={labelCls}>Opis (dla Ciebie w panelu)</label>
                <input
                  data-testid="discount-form-label"
                  className={inputCls}
                  value={editing.label || ""}
                  onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                  placeholder="Promocja wiosenna 2026"
                />
              </div>
              <div>
                <label className={labelCls}>Typ rabatu</label>
                <select
                  data-testid="discount-form-type"
                  className={inputCls}
                  value={editing.discount_type || "percent"}
                  onChange={(e) => setEditing({ ...editing, discount_type: e.target.value })}
                >
                  <option value="percent">Procent (%)</option>
                  <option value="fixed">Kwota (zł)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>
                  Wartość rabatu {editing.discount_type === "percent" ? "(%)" : "(zł)"}
                </label>
                <input
                  data-testid="discount-form-value"
                  className={inputCls}
                  type="number"
                  step="0.01"
                  min="0"
                  value={editing.discount_value ?? ""}
                  onChange={(e) => setEditing({ ...editing, discount_value: e.target.value })}
                />
              </div>
              <div>
                <label className={labelCls}>Ważny do (opcjonalnie)</label>
                <input
                  data-testid="discount-form-valid-until"
                  className={inputCls}
                  type="date"
                  value={(editing.valid_until || "").slice(0, 10)}
                  onChange={(e) => setEditing({ ...editing, valid_until: e.target.value ? e.target.value + "T23:59:59" : "" })}
                />
              </div>
              <div>
                <label className={labelCls}>Limit użyć (puste = nieograniczony)</label>
                <input
                  data-testid="discount-form-usage-limit"
                  className={inputCls}
                  type="number"
                  min="1"
                  value={editing.usage_limit ?? ""}
                  onChange={(e) => setEditing({ ...editing, usage_limit: e.target.value })}
                  placeholder="np. 100"
                />
              </div>
            </div>

            {/* Applies to */}
            <div>
              <label className={labelCls}>Obowiązuje na abonamentach (puste = wszystkie)</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {all_plans.map((p) => {
                  const active = (editing.applies_to || []).includes(p.id);
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => togglePlan(p.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                        active ? "bg-[#1f3a8a] text-white border-[#1f3a8a]" : "bg-white text-slate-700 border-slate-300 hover:border-[#1f3a8a]"
                      }`}
                    >
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="flex items-center gap-2">
              <input
                data-testid="discount-form-active"
                type="checkbox"
                checked={!!editing.active}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                className="accent-[#1f3a8a]"
              />
              <span className="text-sm font-semibold text-slate-700">Kod aktywny</span>
            </label>

            <div className="flex gap-2 pt-2 border-t border-slate-100">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-full border border-slate-300 font-semibold text-slate-700 hover:bg-slate-50">
                Anuluj
              </button>
              <button
                type="button"
                data-testid="discount-form-save"
                onClick={save}
                className="inline-flex items-center gap-1.5 bg-[#1f3a8a] hover:bg-[#15296b] text-white font-semibold px-5 py-2 rounded-full transition"
              >
                <Save size={16} /> {editing.id ? "Zapisz zmiany" : "Utwórz kod"}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
